
import React, { useState, useEffect, useRef } from "react";
import { Box, HStack, Text, VStack, Pressable, useMediaQuery, ScrollView, Spinner, Avatar } from "native-base";
import Svg from "react-native-svg";
import CustomButton from "../../../../components/button";
import InputField from "../../../../components/input";
import Dropdown from "../../../../components/dropdown";
import SearchableDropdown from "../../../../components/searchableDropDown";
import CustomModal from "../../../../components/modal";
import MobileModelSheet from "../../../../components/actionSheet";
import { deleteCardsIconSVG, addIconSVG } from "../../../../utils/svg";
import { COLORS } from "../../../../utils/colors";
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from "../../../../utils/fonts";
import { MOBILE_VIEW_WIDTH } from "../../../../utils/screens";
import { getDropdownDataAPI } from "../../../../api/apis/quicklinks";
import { Toaster } from "../../../../components/toaster";
import { LoaderAnimation } from "../../../../components/spinner";

const PlusButton = ({ onPress, disabled }) => (
    <Pressable
        onPress={disabled ? undefined : (onPress || (() => { }))}
        p={1.5}
        borderRadius="md"
        bg={COLORS.THEME_COLOR}
        ml={1}
        mt='5px'
        opacity={disabled ? 0.5 : 1}
        _pressed={{ bg: COLORS.THEME_COLOR, opacity: 0.7 }}
    >
        <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            {addIconSVG}
        </Svg>
    </Pressable>
);

const createEmptyRow = () => ({
    id: `row-${Date.now()}-${Math.random()}`,
    heads: "",
    head_id: "",
    subHeads: "",
    sub_head_id: "",
    isCustomSubHead: false,
    details: "",
    item_id: "",
    size: "",
    quantity: "",
    price: "",
    remarks: "",
    description: "",
    isMiscellaneous: false,
    showHead: true,
    showSubHead: true,
    showDetails: true,
});

const UpdateBenchmarkModal = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
    const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
    const toaster = Toaster();
    const mobileSheetRef = useRef(null);

    const [addRowCount, setAddRowCount] = useState("5");
    const [rows, setRows] = useState(Array.from({ length: 5 }, () => createEmptyRow()));
    const [fieldErrors, setFieldErrors] = useState({});

    // Dropdown option states - aligning with QuotationsPage
    const [headsOptions, setHeadsOptions] = useState([]);
    const [subHeadsOptionsMap, setSubHeadsOptionsMap] = useState({}); // Map of head_id -> sub_heads options
    const [detailsOptionsMap, setDetailsOptionsMap] = useState({}); // Map of head_id_sub_head_id -> details options
    const [sizeOptionsMap, setSizeOptionsMap] = useState({}); // Map of head_id_sub_head_id_detailValue -> size options
    const [itemSizeMap, setItemSizeMap] = useState({}); // Map of item_id -> size for quick lookup

    // Loading states for cascaded dropdowns
    const [loadingHeads, setLoadingHeads] = useState(false);
    const [loadingSubHeads, setLoadingSubHeads] = useState({}); // keyed by head_id
    const [loadingDetails, setLoadingDetails] = useState({}); // Map of head_id_sub_head_id -> loading state

    useEffect(() => {
        if (isOpen) {
            setRows(Array.from({ length: 5 }, () => createEmptyRow()));
            setAddRowCount("5");
            setFieldErrors({});

            if (headsOptions.length === 0) {
                fetchHeads();
            }

            if (MobileWindow && mobileSheetRef.current) {
                setTimeout(() => {
                    if (mobileSheetRef.current?.scrollToTop) {
                        mobileSheetRef.current.scrollToTop();
                    }
                }, 100);
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
        }
    }, [isOpen]);

    const normalizeText = (value) => (value ?? "").toString().trim();
    const isZeroValue = (value) => {
        const normalized = normalizeText(value).replace(/,/g, "");
        if (!normalized) return false;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) && parsed === 0;
    };

    const addRowCountOptions = [
        { label: "1", value: "1" },
        { label: "5", value: "5" },
        { label: "10", value: "10" },
    ];

    // ── API helpers aligning with QuotationsPage ───────────────────────────────
    const fetchHeads = async () => {
        setLoadingHeads(true);
        try {
            const response = await getDropdownDataAPI('head');
            if (response.error) {
                toaster(response.message || 'Failed to fetch heads', 'error', 'error');
                return;
            }
            const headsData = response.data?.data?.data || [];
            const options = headsData.map((item) => ({
                value: item.head || item.name || item._id,
                label: item.head || item.name || item._id,
                head_id: item.head_id || item.head || item._id,
            }));
            setHeadsOptions(options);
        } catch (error) {
            console.error('Error fetching heads:', error);
        } finally {
            setLoadingHeads(false);
        }
    };

    const fetchSubHeads = async (head_id, rowIndex, showLoader = true) => {
        if (!head_id) return [];
        if (subHeadsOptionsMap[head_id]?.length > 0) return subHeadsOptionsMap[head_id];
        if (loadingSubHeads[head_id]) return [];

        setLoadingSubHeads(prev => ({ ...prev, [head_id]: true }));
        if (showLoader) LoaderAnimation.show();
        try {
            const response = await getDropdownDataAPI('sub_heads', head_id);
            if (response.error) {
                if (showLoader) toaster(response.message || 'Failed to fetch sub heads', 'error', 'error');
                return [];
            }
            const subHeadsData = response.data?.data?.data || [];
            const options = subHeadsData.map((item) => ({
                value: item.sub_head || item.name || item.sub_head_id || item._id,
                label: item.sub_head || item.name || item.sub_head_id || item._id,
                sub_head_id: item.sub_head_id || item.sub_head || item._id,
            }));
            setSubHeadsOptionsMap(prev => ({ ...prev, [head_id]: options }));
            return options;
        } catch (error) {
            console.error('Error fetching sub heads:', error);
            return [];
        } finally {
            setLoadingSubHeads(prev => ({ ...prev, [head_id]: false }));
            if (showLoader) LoaderAnimation.hide();
        }
    };

    const fetchDetails = async (head_id, sub_head_id, rowIndex, showLoader = true) => {
        if (!head_id || !sub_head_id) return [];
        const key = `${head_id}_${sub_head_id}`;
        if (detailsOptionsMap[key]?.length > 0) return detailsOptionsMap[key];
        if (loadingDetails[key]) return [];

        setLoadingDetails(prev => ({ ...prev, [key]: true }));
        if (showLoader) LoaderAnimation.show();
        try {
            const response = await getDropdownDataAPI('item', head_id, sub_head_id);
            if (response.error) {
                if (showLoader) toaster(response.message || 'Failed to fetch details', 'error', 'error');
                return [];
            }

            const headsData = response.data?.data?.data || [];
            let itemsData = [];
            for (const headData of headsData) {
                if (headData.head_id === head_id || headData._id === head_id) {
                    const subHeads = headData.sub_heads || [];
                    for (const subHead of subHeads) {
                        if (subHead.sub_head_id === sub_head_id || subHead._id === sub_head_id) {
                            itemsData = subHead.items || [];
                            break;
                        }
                    }
                    break;
                }
            }

            const options = itemsData.map((item) => ({
                value: item.details || item.name || item.item_id || item._id,
                label: item.details || item.name || item.item_id || item._id,
                item_id: item.item_id || item.details || item._id,
                size: item.size || null
            }));

            const newSizeOptionsMap = { ...sizeOptionsMap };
            const newItemSizeMap = { ...itemSizeMap };
            const detailGroups = {};

            itemsData.forEach((item) => {
                const itemId = item.item_id || item.details || item._id;
                const itemSize = item.size;
                const detailValue = item.details || item.name || item.item_id || item._id;

                if (itemSize) newItemSizeMap[itemId] = itemSize;
                if (!detailGroups[detailValue]) detailGroups[detailValue] = [];
                if (itemSize) detailGroups[detailValue].push(itemSize);
            });

            Object.keys(detailGroups).forEach((detailValue) => {
                const sizeKey = `${key}_${detailValue}`;
                const uniqueSizes = [...new Set(detailGroups[detailValue])];
                if (uniqueSizes.length > 0) {
                    newSizeOptionsMap[sizeKey] = uniqueSizes.map(size => ({ value: size, label: size }));
                }
            });

            setSizeOptionsMap(newSizeOptionsMap);
            setItemSizeMap(newItemSizeMap);
            setDetailsOptionsMap(prev => ({ ...prev, [key]: options }));
            return options;
        } catch (error) {
            console.error('Error fetching details:', error);
            return [];
        } finally {
            setLoadingDetails(prev => ({ ...prev, [key]: false }));
            if (showLoader) LoaderAnimation.hide();
        }
    };

    // ── Row management exactly matching QuotationsPage ────────────────────────
    const updateRow = (index, field, value) => {
        const updatedRows = rows.map((row, i) => {
            if (i === index) {
                const updatedRow = { ...row, [field]: value };

                // When Heads changes
                if (field === "heads") {
                    const selectedHead = headsOptions.find(opt =>
                        opt.value === value ||
                        opt.value?.toLowerCase() === value?.toLowerCase() ||
                        opt.head_id?.toLowerCase() === value?.toLowerCase()
                    );
                    const head_id = selectedHead?.head_id || value || "";
                    const isMisc = head_id?.toLowerCase() === "miscellaneous" || value?.toLowerCase() === "miscellaneous";

                    updatedRow.head_id = isMisc ? "miscellaneous" : head_id;
                    updatedRow.heads = value;
                    updatedRow.showHead = true;
                    updatedRow.isMiscellaneous = isMisc;

                    if (isMisc) {
                        updatedRow.subHeads = "";
                        updatedRow.sub_head_id = "";
                        updatedRow.isCustomSubHead = false;
                        updatedRow.details = "";
                        updatedRow.item_id = "";
                        updatedRow.size = "";
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.remarks = "";
                        updatedRow.description = "";
                        updatedRow.showSubHead = false;
                        updatedRow.showDetails = false;
                    } else {
                        updatedRow.description = "";
                        updatedRow.subHeads = undefined;
                        updatedRow.sub_head_id = "";
                        updatedRow.isCustomSubHead = false;
                        updatedRow.details = undefined;
                        updatedRow.item_id = "";
                        updatedRow.size = undefined;
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.remarks = "";
                        updatedRow.showSubHead = true;
                        updatedRow.showDetails = true;
                        if (head_id) {
                            fetchSubHeads(head_id, index);
                        }
                    }
                }
                // When SubHeads changes
                else if (field === "subHeads") {
                    const head_id = updatedRow.head_id;
                    const isMisc = updatedRow.isMiscellaneous;

                    if (isMisc) {
                        updatedRow.subHeads = value;
                        updatedRow.sub_head_id = "";
                        updatedRow.isCustomSubHead = true;
                    } else {
                        if (!value) {
                            updatedRow.subHeads = "";
                            updatedRow.sub_head_id = "";
                            updatedRow.isCustomSubHead = false;
                            updatedRow.details = undefined;
                            updatedRow.item_id = "";
                            updatedRow.size = undefined;
                            updatedRow.quantity = "";
                            updatedRow.price = "";
                            updatedRow.remarks = "";
                            updatedRow.showSubHead = true;
                            updatedRow.showDetails = true;
                            return updatedRow;
                        }

                        const selectedSubHead = subHeadsOptionsMap[head_id]?.find(opt =>
                            opt.value === value ||
                            opt.value?.toString()?.toLowerCase() === value?.toString()?.toLowerCase() ||
                            opt.sub_head_id?.toString()?.toLowerCase() === value?.toString()?.toLowerCase()
                        );
                        const sub_head_id = selectedSubHead?.sub_head_id || value || "";
                        updatedRow.subHeads = selectedSubHead?.value || value;
                        updatedRow.sub_head_id = sub_head_id;
                        updatedRow.isCustomSubHead = !selectedSubHead;
                        updatedRow.showSubHead = true;
                        updatedRow.details = undefined;
                        updatedRow.item_id = "";
                        updatedRow.size = undefined;
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.remarks = "";
                        updatedRow.showDetails = true;
                        if (head_id && sub_head_id) {
                            fetchDetails(head_id, sub_head_id, index);
                        }
                    }
                }
                // When Details changes
                else if (field === "details") {
                    const head_id = updatedRow.head_id;
                    const isMisc = updatedRow.isMiscellaneous;

                    if (isMisc) {
                        updatedRow.details = value;
                        updatedRow.item_id = "";
                    } else {
                        if (!value) {
                            updatedRow.details = "";
                            updatedRow.item_id = "";
                            updatedRow.size = undefined;
                            updatedRow.quantity = "";
                            updatedRow.price = "";
                            updatedRow.remarks = "";
                            updatedRow.showDetails = true;
                            return updatedRow;
                        }

                        const sub_head_id = updatedRow.sub_head_id;
                        const key = `${head_id}_${sub_head_id}`;
                        const selectedDetail = detailsOptionsMap[key]?.find(opt =>
                            opt.value === value ||
                            opt.value?.toString()?.toLowerCase() === value?.toString()?.toLowerCase() ||
                            opt.item_id?.toString()?.toLowerCase() === value?.toString()?.toLowerCase()
                        );
                        const item_id = selectedDetail?.item_id || value || "";
                        updatedRow.details = selectedDetail?.value || value;
                        updatedRow.item_id = item_id;
                        updatedRow.showDetails = true;

                        updatedRow.size = undefined;
                        updatedRow.quantity = "";
                        updatedRow.price = "";
                        updatedRow.remarks = "";
                    }
                }
                // When Size changes
                else if (field === "size") {
                    updatedRow.size = value;
                }
                return updatedRow;
            }
            return row;
        });
        setRows(updatedRows);

        if (field === "quantity" || field === "price") {
            const rowId = updatedRows[index]?.id;
            if (!rowId) return;
            const hasZero = isZeroValue(value);
            setFieldErrors((prev) => ({
                ...prev,
                [rowId]: { ...prev[rowId], [field]: hasZero }
            }));
        }
    };

    const handleAddRows = () => {
        const count = parseInt(addRowCount) || 1;
        const newRows = Array.from({ length: count }, () => createEmptyRow());
        setRows([...rows, ...newRows]);
    };

    const removeRow = (index) => {
        const newRows = [...rows];
        const removedRow = newRows[index];
        newRows.splice(index, 1);
        setRows(newRows);
        if (removedRow?.id) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[removedRow.id];
                return next;
            });
        }
    };

    // ── Plus-button helpers precisely matching QuotationsPage behavior ─────────
    const addRowWithSameHead = (index) => {
        const currentRow = rows[index];
        if (!currentRow?.heads || !currentRow?.head_id) return;

        const newRow = {
            ...createEmptyRow(),
            heads: currentRow.heads,
            head_id: currentRow.head_id,
            showHead: false,
            showSubHead: true,
            showDetails: true,
            isMiscellaneous: currentRow.isMiscellaneous,
        };

        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);

        if (currentRow.head_id && !currentRow.isMiscellaneous) {
            fetchSubHeads(currentRow.head_id, index + 1, false);
        }
    };

    const addRowWithSameHeadSubHead = (index) => {
        const currentRow = rows[index];
        if (!currentRow?.heads || !currentRow?.subHeads) return;

        const newRow = {
            ...createEmptyRow(),
            heads: currentRow.heads,
            head_id: currentRow.head_id,
            subHeads: currentRow.subHeads,
            sub_head_id: currentRow.sub_head_id,
            isCustomSubHead: currentRow.isCustomSubHead,
            showHead: false,
            showSubHead: false,
            showDetails: true,
            isMiscellaneous: currentRow.isMiscellaneous,
        };

        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);

        if (currentRow.head_id && currentRow.sub_head_id && !currentRow.isMiscellaneous) {
            fetchDetails(currentRow.head_id, currentRow.sub_head_id, index + 1, false);
        }
    };

    const addRowWithSameDetails = (index) => {
        const currentRow = rows[index];
        if (!currentRow?.heads || !currentRow?.subHeads || !currentRow?.details) return;

        const newRow = {
            ...createEmptyRow(),
            heads: currentRow.heads,
            head_id: currentRow.head_id,
            subHeads: currentRow.subHeads,
            sub_head_id: currentRow.sub_head_id,
            isCustomSubHead: currentRow.isCustomSubHead,
            details: currentRow.details,
            item_id: currentRow.item_id,
            showHead: false,
            showSubHead: false,
            showDetails: false,
            isMiscellaneous: currentRow.isMiscellaneous,
        };

        const newRows = [...rows];
        newRows.splice(index + 1, 0, newRow);
        setRows(newRows);
    };

    const handleSubmit = () => {
        const nextErrors = {};
        rows.forEach((row) => {
            const quantityZero = isZeroValue(row.quantity);
            const priceZero = isZeroValue(row.price);
            if (quantityZero || priceZero) {
                nextErrors[row.id] = {
                    quantity: quantityZero,
                    price: priceZero
                };
            }
        });
        setFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        onSubmit(rows);
    };

    const getModalHeader = () => (
        <Text fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}>
            Update Existing Benchmark
        </Text>
    );

    const getModalBody = () => (
        <ScrollView showsVerticalScrollIndicator={false}>
            <Box>
                <HStack justifyContent="space-between" alignItems="center" mb={3} flexWrap={MobileWindow ? "wrap" : "nowrap"} space={3}>
                    <Text fontSize={COMMONFONTSIZE.LARGE_MEDIUM_FONT_SIZE} fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}>
                        Benchmark Details
                    </Text>
                    <HStack alignItems="center" space={2} flexWrap={MobileWindow ? "wrap" : "nowrap"}>
                        <Box width={MobileWindow ? "100px" : "80px"}>
                            <Dropdown
                                name="add_row_count"
                                selectedValues={addRowCount}
                                setSelectedValues={setAddRowCount}
                                dropItems={addRowCountOptions}
                                placeholder="5"
                                width="100%"
                            />
                        </Box>
                        <CustomButton
                            text={`Add ${addRowCount} Row${parseInt(addRowCount) > 1 ? 's' : ''}`}
                            onPress={handleAddRows}
                            bgColor={COLORS.THEME_COLOR}
                            textColor={COLORS.WHITE_COLOR}
                            size="sm"
                        />
                    </HStack>
                </HStack>

                <Box
                    borderWidth={1}
                    borderColor={COLORS.BORDER_COLOR}
                    borderRadius="md"
                    overflow="hidden"
                    bg={COLORS.WHITE_COLOR}
                >
                    <HStack
                        bg={COLORS.BORDER_COLOR}
                        py={2}
                        px={2}
                        borderBottomWidth={1}
                        borderBottomColor={COLORS.BORDER_COLOR}
                        display={MobileWindow ? "none" : "flex"}
                    >
                        <Box flex={2} px={2}><Text fontSize="sm" fontWeight="bold">Head</Text></Box>
                        <Box flex={1.5} px={2}><Text fontSize="sm" fontWeight="bold">Sub Head</Text></Box>
                        <Box flex={1.5} px={2}><Text fontSize="sm" fontWeight="bold">Details</Text></Box>
                        <Box flex={1} px={2}><Text fontSize="sm" fontWeight="bold">Size</Text></Box>
                        <Box flex={1} px={2}><Text fontSize="sm" fontWeight="bold">Quantity</Text></Box>
                        <Box flex={1.5} px={2}><Text fontSize="sm" fontWeight="bold">Price</Text></Box>
                        <Box flex={1.5} px={2}><Text fontSize="sm" fontWeight="bold">Remarks</Text></Box>
                        {rows.length > 1 && (
                            <Box flex={0.5} px={2} alignItems="center" justifyContent="center">
                                <Text fontSize="sm" fontWeight="bold">Action</Text>
                            </Box>
                        )}
                    </HStack>

                    <VStack>
                        {rows.map((row, index) => (
                            <Box
                                key={row.id || `row-${index}`}
                                borderBottomWidth={index < rows.length - 1 ? 1 : 0}
                                borderBottomColor={COLORS.BORDER_COLOR}
                                bg={index % 2 === 0 ? COLORS.WHITE_COLOR : "gray.50"}
                            >
                                {MobileWindow ? (
                                    <VStack p={3} space={3}>
                                        <HStack justifyContent="space-between" alignItems="center">
                                            <Text fontSize="sm" fontWeight="bold" flex={1}>Row {index + 1}</Text>
                                            {rows.length > 1 && (
                                                <Pressable onPress={() => removeRow(index)} p={1} borderRadius="md" _pressed={{ bg: COLORS.BORDER_COLOR }}>
                                                    <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">{deleteCardsIconSVG}</Svg>
                                                </Pressable>
                                            )}
                                        </HStack>
                                        <HStack space={2} alignItems="flex-end">
                                            <Box flex={1}>
                                                {row.showHead !== false ? (
                                                    <SearchableDropdown
                                                        label="Heads"
                                                        name={`heads_${index}`}
                                                        selectedValues={row.heads}
                                                        setSelectedValues={(value) => updateRow(index, "heads", value)}
                                                        dropItems={headsOptions}
                                                        placeholder="Head"
                                                        width="100%"
                                                    />
                                                ) : (
                                                    <Box>
                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Heads</Text>
                                                        <Box height="36px" />
                                                    </Box>
                                                )}
                                            </Box>
                                            {row.showHead !== false && (
                                                <PlusButton onPress={() => addRowWithSameHead(index)} disabled={!row.heads || !row.head_id} />
                                            )}
                                        </HStack>
                                        {row.isMiscellaneous ? (
                                            <>
                                                <InputField label="SubHeads" name={`subHeads_${index}`} value={row.subHeads || ""} onChange={(e) => updateRow(index, "subHeads", e.target.value)} placeholder="Enter subhead" width="100%" />
                                                <InputField label="Details" name={`details_${index}`} value={row.details || ""} onChange={(e) => updateRow(index, "details", e.target.value)} placeholder="Enter details" width="100%" />
                                                <HStack space={2}>
                                                    <Box flex={1}><InputField label="Size" name={`size_${index}`} value={row.size || ""} onChange={(e) => updateRow(index, "size", e.target.value)} placeholder="Enter size" width="100%" /></Box>
                                                    <Box flex={1}><InputField label="Quantity" name={`quantity_${index}`} value={row.quantity || ""} onChange={(e) => updateRow(index, "quantity", e.target.value)} placeholder="Enter quantity" keyboardType="numeric" width="100%" isInvalid={!!fieldErrors[row.id]?.quantity} errorMessage="Invalid" /></Box>
                                                </HStack>
                                                <InputField label="Price" name={`price_${index}`} value={row.price || ""} onChange={(e) => updateRow(index, "price", e.target.value)} placeholder="Enter price" keyboardType="numeric" width="100%" isInvalid={!!fieldErrors[row.id]?.price} errorMessage="Invalid" />
                                                <InputField label="Remarks" name={`remarks_${index}`} value={row.remarks || ""} onChange={(e) => updateRow(index, "remarks", e.target.value)} placeholder="Enter remarks" width="100%" />
                                            </>
                                        ) : (
                                            <>
                                                <HStack space={2} alignItems="flex-end">
                                                    <Box flex={1}>
                                                        {row.showSubHead !== false ? (
                                                            <SearchableDropdown
                                                                label="SubHeads"
                                                                name={`subHeads_${index}`}
                                                                selectedValues={row.subHeads}
                                                                setSelectedValues={(value) => updateRow(index, "subHeads", value)}
                                                                dropItems={row.head_id ? (subHeadsOptionsMap[row.head_id] || []) : []}
                                                                placeholder="Sub Head"
                                                                allowCustomValue={true}
                                                                isClearable={true}
                                                                width="100%"
                                                                isDisabled={!row.head_id}
                                                            />
                                                        ) : (
                                                            <Box><Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>SubHeads</Text><Box height="36px" /></Box>
                                                        )}
                                                    </Box>
                                                    {row.showSubHead !== false && (
                                                        <PlusButton onPress={() => addRowWithSameHeadSubHead(index)} disabled={!row.heads || !row.subHeads} />
                                                    )}
                                                </HStack>
                                                <HStack space={2} alignItems="flex-end">
                                                    <Box flex={1}>
                                                        {row.showDetails !== false ? (
                                                            <SearchableDropdown
                                                                label="Details"
                                                                name={`details_${index}`}
                                                                selectedValues={row.details}
                                                                setSelectedValues={(value) => updateRow(index, "details", value)}
                                                                dropItems={row.head_id && row.sub_head_id ? (detailsOptionsMap[`${row.head_id}_${row.sub_head_id}`] || []) : []}
                                                                placeholder="Details"
                                                                allowCustomValue={true}
                                                                isClearable={true}
                                                                width="100%"
                                                                isDisabled={!row.sub_head_id}
                                                            />
                                                        ) : (
                                                            <Box><Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Details</Text><Box height="36px" /></Box>
                                                        )}
                                                    </Box>
                                                    {row.showDetails !== false && (
                                                        <PlusButton onPress={() => addRowWithSameDetails(index)} disabled={!row.heads || !row.subHeads || !row.details} />
                                                    )}
                                                </HStack>
                                                <HStack space={2}>
                                                    <Box flex={1}>
                                                        <Text fontSize="xs" color={COLORS.LABEL_TEXT_COLOR} mb={1}>Size</Text>
                                                        <SearchableDropdown
                                                            name={`size_${index}`}
                                                            selectedValues={row.size}
                                                            setSelectedValues={(value) => updateRow(index, "size", value)}
                                                            dropItems={row.head_id && row.sub_head_id && row.details ? (sizeOptionsMap[`${row.head_id}_${row.sub_head_id}_${row.details}`] || []) : []}
                                                            placeholder="Size"
                                                            allowCustomValue={true}
                                                            isClearable={true}
                                                            width="100%"
                                                            isDisabled={!row.head_id || !row.sub_head_id || !row.details || row.isMiscellaneous}
                                                        />
                                                    </Box>
                                                    <Box flex={1}><InputField label="Quantity" name={`quantity_${index}`} value={row.quantity || ""} onChange={(e) => updateRow(index, "quantity", e.target.value)} placeholder="Quantity" keyboardType="numeric" width="100%" isInvalid={!!fieldErrors[row.id]?.quantity} errorMessage="Invalid" /></Box>
                                                </HStack>
                                                <InputField label="Price" name={`price_${index}`} value={row.price || ""} onChange={(e) => updateRow(index, "price", e.target.value)} placeholder="Price" keyboardType="numeric" width="100%" isInvalid={!!fieldErrors[row.id]?.price} errorMessage="Invalid" />
                                                <InputField label="Remarks" name={`remarks_${index}`} value={row.remarks || ""} onChange={(e) => updateRow(index, "remarks", e.target.value)} placeholder="Remarks" width="100%" />
                                            </>
                                        )}
                                    </VStack>
                                ) : (
                                    <HStack py={3} px={2} alignItems="flex-start" space={0}>
                                        <Box flex={2} px={1}>
                                            <HStack space={1} alignItems="center">
                                                <Box flex={1}>
                                                    {row.showHead !== false ? (
                                                        <SearchableDropdown
                                                            name={`heads_${index}`}
                                                            selectedValues={row.heads}
                                                            setSelectedValues={(value) => updateRow(index, "heads", value)}
                                                            dropItems={headsOptions}
                                                            placeholder="Head"
                                                            width="100%"
                                                        />
                                                    ) : <Box height="36px" />}
                                                </Box>
                                                {row.showHead !== false && (
                                                    <PlusButton onPress={() => addRowWithSameHead(index)} disabled={!row.heads || !row.head_id} />
                                                )}
                                            </HStack>
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            {row.isMiscellaneous ? (
                                                <InputField name={`subHeads_${index}`} value={row.subHeads || ""} onChange={(e) => updateRow(index, "subHeads", e.target.value)} placeholder="Enter subhead" width="100%" />
                                            ) : (
                                                <HStack space={1} alignItems="center">
                                                    <Box flex={1}>
                                                        {row.showSubHead !== false ? (
                                                            <SearchableDropdown
                                                                name={`subHeads_${index}`}
                                                                selectedValues={row.subHeads}
                                                                setSelectedValues={(value) => updateRow(index, "subHeads", value)}
                                                                dropItems={row.head_id ? (subHeadsOptionsMap[row.head_id] || []) : []}
                                                                placeholder="Sub Head"
                                                                allowCustomValue={true}
                                                                isClearable={true}
                                                                width="100%"
                                                                isDisabled={!row.head_id}
                                                            />
                                                        ) : <Box height="36px" />}
                                                    </Box>
                                                    {row.showSubHead !== false && (
                                                        <PlusButton onPress={() => addRowWithSameHeadSubHead(index)} disabled={!row.heads || !row.subHeads} />
                                                    )}
                                                </HStack>
                                            )}
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            {row.isMiscellaneous ? (
                                                <InputField name={`details_${index}`} value={row.details || ""} onChange={(e) => updateRow(index, "details", e.target.value)} placeholder="Enter details" width="100%" />
                                            ) : (
                                                <HStack space={1} alignItems="center">
                                                    <Box flex={1}>
                                                        {row.showDetails !== false ? (
                                                            <SearchableDropdown
                                                                name={`details_${index}`}
                                                                selectedValues={row.details}
                                                                setSelectedValues={(value) => updateRow(index, "details", value)}
                                                                dropItems={row.head_id && row.sub_head_id ? (detailsOptionsMap[`${row.head_id}_${row.sub_head_id}`] || []) : []}
                                                                placeholder="Details"
                                                                allowCustomValue={true}
                                                                isClearable={true}
                                                                width="100%"
                                                                isDisabled={!row.sub_head_id}
                                                            />
                                                        ) : <Box height="36px" />}
                                                    </Box>
                                                    {row.showDetails !== false && (
                                                        <PlusButton onPress={() => addRowWithSameDetails(index)} disabled={!row.heads || !row.subHeads || !row.details} />
                                                    )}
                                                </HStack>
                                            )}
                                        </Box>
                                        <Box flex={1} px={1}>
                                            {row.isMiscellaneous ? (
                                                <InputField name={`size_${index}`} value={row.size || ""} onChange={(e) => updateRow(index, "size", e.target.value)} placeholder="Enter size" width="100%" />
                                            ) : (
                                                <SearchableDropdown
                                                    name={`size_${index}`}
                                                    selectedValues={row.size}
                                                    setSelectedValues={(value) => updateRow(index, "size", value)}
                                                    dropItems={row.head_id && row.sub_head_id && row.details ? (sizeOptionsMap[`${row.head_id}_${row.sub_head_id}_${row.details}`] || []) : []}
                                                    placeholder="Size"
                                                    allowCustomValue={true}
                                                    isClearable={true}
                                                    width="100%"
                                                    isDisabled={!row.head_id || !row.sub_head_id || !row.details || row.isMiscellaneous}
                                                />
                                            )}
                                        </Box>
                                        <Box flex={1} px={1}>
                                            <InputField name={`quantity_${index}`} value={row.quantity || ""} onChange={(e) => updateRow(index, "quantity", e.target.value)} placeholder="Quantity" keyboardType="numeric" width="100%" isInvalid={!!fieldErrors[row.id]?.quantity} errorMessage="Invalid" />
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            <InputField name={`price_${index}`} value={row.price || ""} onChange={(e) => updateRow(index, "price", e.target.value)} placeholder="Price" keyboardType="numeric" width="100%" isInvalid={!!fieldErrors[row.id]?.price} errorMessage="Invalid" />
                                        </Box>
                                        <Box flex={1.5} px={1}>
                                            <InputField name={`remarks_${index}`} value={row.remarks || ""} onChange={(e) => updateRow(index, "remarks", e.target.value)} placeholder="Remarks" width="100%" />
                                        </Box>
                                        {rows.length > 1 && (
                                            <Box flex={0.5} px={1} alignItems="center" justifyContent="center">
                                                <Pressable onPress={() => removeRow(index)} p={1} borderRadius="md" _pressed={{ bg: COLORS.BORDER_COLOR }}>
                                                    <Svg width="20" height="20" viewBox="0 0 24 24" fill="none">{deleteCardsIconSVG}</Svg>
                                                </Pressable>
                                            </Box>
                                        )}
                                    </HStack>
                                )}
                            </Box>
                        ))}
                    </VStack>
                </Box>
            </Box>
        </ScrollView>
    );

    const getModalFooter = () => (
        <HStack space={3} justifyContent="flex-end" width="100%">
            <CustomButton text="Discard Changes" onPress={onClose} isOutline borderColor={COLORS.THEME_COLOR} textColor={COLORS.THEME_COLOR} />
            <CustomButton text="Submit" onPress={handleSubmit} isLoading={isLoading} bgColor={COLORS.THEME_COLOR} textColor={COLORS.WHITE_COLOR} />
        </HStack>
    );

    return (
        <>
            {!MobileWindow ? (
                <CustomModal isOpen={isOpen} onClose={onClose} header={getModalHeader()} body={getModalBody()} footer={getModalFooter()} maxW="1600px" maxH="80vh" width="100%" />
            ) : (
                <MobileModelSheet ref={mobileSheetRef} isOpen={isOpen} onClose={onClose} header={getModalHeader()} body={getModalBody()} footer={getModalFooter()} maxH="85vh" />
            )}
        </>
    );
};

export default UpdateBenchmarkModal;
